import { clamp } from '../core/math';
import { Random } from '../core/Random';
import { ArmWrestleGame } from '../minigames/armwrestle';
import type { DuelGame, DuelMsg } from '../net/protocol';
import type { Neighbor } from '../net/Session';
import { ArmStage, type Holdable } from '../stages/ArmStage';
import { DiceStage } from '../stages/DiceStage';
import type { StageHost } from '../ui/StageHost';
import { spoolTable } from '../world/builders/gameProps';
import type { Vec2 } from '../world/layout';
import type { GameServices } from './services';

/** Random hex string from the browser's crypto (falls back for old engines). */
export function randomHex(bytes = 16): string {
  const a = new Uint8Array(bytes);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(a);
  else for (let i = 0; i < bytes; i++) a[i] = Math.floor(Math.random() * 256);
  return [...a].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** SHA-256 hex of a string. */
export async function sha256(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const hash = await globalThis.crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Two dice each, rolled from both players' secrets: neither side could pick theirs
 * after seeing the other's, so neither side can rig it.
 */
export function rollDice(
  inviterNonce: string,
  inviteeNonce: string,
): { inviter: [number, number]; invitee: [number, number] } {
  let h = 0x811c9dc5;
  for (const ch of `${inviterNonce}:${inviteeNonce}`) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  const rng = new Random(h);
  const d = (): number => rng.int(1, 6);
  return { inviter: [d(), d()], invitee: [d(), d()] };
}

const TIMEOUT_MS = 7000;
const INVITE_MS = 20000;

interface Duel {
  id: number;
  peer: string;
  name: string;
  game: DuelGame;
  bet: number;
  role: 'inviter' | 'invitee';
  /** Inviter: our secret. Invitee: theirs, once revealed. */
  nonce: string;
  /** Invitee: the inviter's commitment. */
  commit: string;
  /** Invitee: our secret (sent in the accept). */
  myNonce: string;
  paid: boolean;
  settled: boolean;
  createdAt: number;
  heardAt: number;
  arm?: { game: ArmWrestleGame; a: number; theirPush: number; sendIn: number };
}

export interface DuelHooks {
  send(peer: string, msg: DuelMsg): void;
  neighbor(id: string): Neighbor | undefined;
  /** Their character in our world (to sit across the table from), and where they're standing. */
  avatar(id: string): (Holdable & { at(): Vec2 | null }) | null;
  /** In a menu, a mini-game, asleep... can't take a challenge right now. */
  busy(): boolean;
}

/**
 * Player-vs-player challenges in a multiplayer park: arm wrestling (live, mash to
 * push) and shooting dice. Both sides put up the bet when the challenge is accepted;
 * the winner takes the pot. Runs on the honor system between friends, but dice are
 * rolled from both players' secrets so nobody can rig the roll.
 */
export class Duels {
  private duel: Duel | null = null;
  private nextId = 1;

  constructor(
    private readonly s: GameServices,
    private readonly host: StageHost,
    private readonly hooks: DuelHooks,
  ) {}

  get active(): boolean {
    return this.duel !== null;
  }

  private clock(): number {
    return performance.now();
  }

  /** Challenge a neighbor. */
  async invite(n: Neighbor, game: DuelGame, bet: number): Promise<void> {
    const { store } = this.s;
    if (this.duel) {
      store.toast('Finish the challenge you already got going.', 'info', ':handshake:');
      return;
    }
    if (!store.canAfford(bet)) {
      store.toast(`You'd need $${bet} to back that up.`, 'bad', ':money-wings:');
      return;
    }
    const nonce = randomHex();
    const id = this.nextId++;
    const now = this.clock();
    this.duel = {
      id,
      peer: n.id,
      name: n.name,
      game,
      bet,
      role: 'inviter',
      nonce,
      commit: '',
      myNonce: '',
      paid: false,
      settled: false,
      createdAt: now,
      heardAt: now,
    };
    const commit = await sha256(nonce);
    if (this.duel?.id !== id) return;
    this.hooks.send(n.id, { t: 'invite', id, game, bet, commit });
    store.toast(
      `You challenged ${n.name} to ${game === 'arm' ? 'arm wrestle' : 'shoot dice'} for $${bet}. Waiting on 'em...`,
      'info',
      game === 'arm' ? ':flex:' : ':dice:',
    );
  }

  onMessage(from: Neighbor, msg: DuelMsg): void {
    const d = this.duel;
    if (msg.t === 'invite') return this.onInvite(from, msg);
    if (!d || d.peer !== from.id || d.id !== msg.id) return;
    d.heardAt = this.clock();
    switch (msg.t) {
      case 'decline':
        this.end(
          msg.why === 'busy'
            ? `${d.name} is busy right now. Try 'em in a minute.`
            : msg.why === 'broke'
              ? `${d.name} can't cover the $${d.bet}. Bless their heart.`
              : `${d.name} said nah. Chicken.`,
        );
        return;
      case 'cancel':
        this.refund();
        this.end(`${d.name} called it off. Your money's back.`);
        return;
      case 'accept':
        if (d.role === 'inviter') void this.onAccept(d, msg.nonce);
        return;
      case 'reveal':
        if (d.role === 'invitee' && d.game === 'dice') void this.onReveal(d, msg.nonce);
        return;
      case 'push':
        if (d.arm && d.role === 'inviter') d.arm.theirPush = msg.v;
        return;
      case 'arm':
        if (d.arm && d.role === 'invitee') d.arm.a = msg.a;
        return;
      case 'end':
        if (d.arm) {
          d.arm.a = msg.a;
          d.arm.game.finish(d.role === 'inviter' ? msg.a : -msg.a);
          this.settleArm(d, msg.a);
        }
        return;
    }
  }

  private onInvite(from: Neighbor, msg: Extract<DuelMsg, { t: 'invite' }>): void {
    const { store, ui } = this.s;
    if (this.duel || this.hooks.busy()) {
      this.hooks.send(from.id, { t: 'decline', id: msg.id, why: 'busy' });
      return;
    }
    const now = this.clock();
    const d: Duel = {
      id: msg.id,
      peer: from.id,
      name: from.name,
      game: msg.game,
      bet: msg.bet,
      role: 'invitee',
      nonce: '',
      commit: msg.commit,
      myNonce: randomHex(),
      paid: false,
      settled: false,
      createdAt: now,
      heardAt: now,
    };
    this.duel = d;
    this.s.sfx('whistle');
    ui.modal.show(
      {
        title: msg.game === 'arm' ? 'Arm Wrestle?' : 'Shoot Dice?',
        art: msg.game === 'arm' ? ':flex:' : ':dice:',
        text:
          msg.game === 'arm'
            ? `${from.name} slaps their elbow on the nearest flat surface. "Arm wrestle. $${msg.bet}. Winner takes the pot."`
            : `${from.name} shakes two dice in a cupped hand. "High roll takes it. $${msg.bet} a man. You in?"`,
        choices: [
          {
            label: msg.bet > 0 ? `You're on ($${msg.bet})` : "You're on",
            requires: () => store.canAfford(msg.bet) || `Need $${msg.bet}`,
            run: () => {
              this.accept(d);
            },
          },
        ],
        leave: 'Nah',
      },
      () => {
        if (this.duel === d && !d.paid) {
          this.hooks.send(d.peer, { t: 'decline', id: d.id, why: 'no' });
          this.duel = null;
        }
      },
    );
  }

  private accept(d: Duel): void {
    const { store } = this.s;
    if (this.duel !== d) return;
    if (!store.spend(d.bet)) {
      this.hooks.send(d.peer, { t: 'decline', id: d.id, why: 'broke' });
      this.duel = null;
      return;
    }
    d.paid = true;
    d.heardAt = this.clock();
    this.hooks.send(d.peer, { t: 'accept', id: d.id, nonce: d.myNonce });
    if (d.game === 'arm') window.setTimeout(() => this.startArm(d), 0);
    else store.toast(`${d.name} is rolling...`, 'info', ':dice:');
  }

  private async onAccept(d: Duel, theirNonce: string): Promise<void> {
    const { store } = this.s;
    if (!store.spend(d.bet)) {
      this.hooks.send(d.peer, { t: 'cancel', id: d.id });
      this.end(`You couldn't cover the $${d.bet} anymore. Called it off.`);
      return;
    }
    d.paid = true;
    if (d.game === 'arm') {
      this.startArm(d);
      return;
    }
    this.hooks.send(d.peer, { t: 'reveal', id: d.id, nonce: d.nonce });
    this.showDice(d, rollDice(d.nonce, theirNonce));
  }

  private async onReveal(d: Duel, nonce: string): Promise<void> {
    const ok = (await sha256(nonce)) === d.commit;
    if (this.duel !== d) return;
    if (!ok) {
      this.refund();
      this.end(`${d.name}'s dice came up funny. Something's off. You take your money back.`);
      return;
    }
    this.showDice(d, rollDice(nonce, d.myNonce));
  }

  private showDice(d: Duel, roll: ReturnType<typeof rollDice>): void {
    const { store, ui } = this.s;
    const mine = d.role === 'inviter' ? roll.inviter : roll.invitee;
    const theirs = d.role === 'inviter' ? roll.invitee : roll.inviter;
    const a = mine[0] + mine[1];
    const b = theirs[0] + theirs[1];
    const face = (n: number): string => '⚀⚁⚂⚃⚄⚅'[n - 1] ?? '?';
    let text = `You roll ${face(mine[0])} ${face(mine[1])} (${a}). ${d.name} rolls ${face(theirs[0])} ${face(theirs[1])} (${b}).\n\n`;
    if (a === b) {
      store.earn(d.bet);
      text += 'Push. Everybody gets their money back and nobody is happy about it.';
    } else if (a > b) {
      store.earn(d.bet * 2);
      store.incr('duelWins');
      store.addClout(3);
      this.s.sfx('cash');
      text +=
        d.bet > 0
          ? `You scoop up $${d.bet * 2}. ${d.name} says the dice are "cold." They are not.`
          : "Bragging rights. Use them responsibly. (You won't.)";
    } else {
      this.s.sfx('fail');
      text += `${d.name} takes the pot and blows on the dice like they're hot. Rude.`;
    }
    d.settled = true;
    this.duel = null;
    const them = this.hooks.avatar(d.peer)?.at() ?? null;
    const stage = new DiceStage(
      this.s.player,
      them,
      mine,
      theirs,
      d.name,
      this.s.world.dynamic,
      this.s.rng,
      (n) => this.s.sfx(n),
    );
    if (!this.host.isOpen) {
      this.host.open(stage, () => ({ title: 'Shootin’ Dice', art: ':dice:', text }));
      return;
    }
    ui.modal.show({
      title: 'Shootin’ Dice',
      art: ':dice:',
      text,
      choices: [],
      leave: a > b ? 'Pay up' : 'Next time',
    });
  }

  private startArm(d: Duel): void {
    const { store } = this.s;
    const power =
      1 + Math.min(0.3, store.rating() / 500) - clamp((store.stat('buzz') - 60) / 40, 0, 1) * 0.25;
    const game = new ArmWrestleGame(d.name, null, power);
    game.remoteArm = 0;
    d.arm = { game, a: 0, theirPush: 0, sendIn: 0 };
    d.heardAt = this.clock();
    // Somebody rolls out a cable spool between the two of you and you sit down across it.
    const me = this.s.player.position;
    const rival = this.hooks.avatar(d.peer);
    const at = rival?.at() ?? {
      x: me.x + Math.sin(this.s.player.facing) * 1.5,
      z: me.z + Math.cos(this.s.player.facing) * 1.5,
    };
    const len = Math.hypot(at.x - me.x, at.z - me.z) || 1;
    const dir = { x: (at.x - me.x) / len, z: (at.z - me.z) / len };
    const table = { x: (me.x + at.x) / 2, z: (me.z + at.z) / 2 };
    const spool = spoolTable();
    spool.rotation.y = -Math.atan2(dir.z, dir.x);
    const stage = new ArmStage(
      game,
      {
        table,
        tableY: 0.63,
        you: { x: table.x - dir.x * 0.78, z: table.z - dir.z * 0.78 },
        them: { x: table.x + dir.x * 0.78, z: table.z + dir.z * 0.78 },
        seat: 0.46,
        rival,
        prop: { object: spool, parent: this.s.world.dynamic },
      },
      this.s.player,
      (n) => this.s.sfx(n),
    );
    this.host.open(stage, (st, bailed) => {
      const g = (st as ArmStage).game;
      const arm = d.arm;
      if (bailed && arm && !d.settled) {
        // Walking away is a loss.
        const a = d.role === 'inviter' ? -1 : 1;
        this.hooks.send(d.peer, { t: 'end', id: d.id, a });
        this.settleArm(d, a);
      }
      const won = g.arm > 0;
      return {
        title: won ? 'PINNED ’EM!' : 'Pinned',
        art: ':flex:',
        text: won
          ? `${d.name}'s knuckles hit the table.${d.bet ? ` $${d.bet * 2} is yours.` : ''} They want a rematch. Of course they do.`
          : `${d.name} pins you and does a little dance about it.${d.bet ? ` There goes $${d.bet}.` : ''}`,
      };
    });
  }

  private settleArm(d: Duel, a: number): void {
    if (d.settled) return;
    d.settled = true;
    const won = d.role === 'inviter' ? a > 0 : a < 0;
    if (won) {
      this.s.store.earn(d.bet * 2);
      this.s.store.incr('duelWins');
      this.s.store.addClout(5);
      this.s.store.unlock('arm_wrestle');
    }
    this.duel = null;
  }

  /** Per frame: the live part of arm wrestling, and timeouts. */
  update(dt: number): void {
    const d = this.duel;
    if (!d) return;
    const now = this.clock();
    if (!d.paid && now - d.createdAt > INVITE_MS) {
      if (d.role === 'inviter') this.hooks.send(d.peer, { t: 'cancel', id: d.id });
      this.end(`${d.name} never answered. Challenge expired.`);
      return;
    }
    if (!this.hooks.neighbor(d.peer) || (d.paid && now - d.heardAt > TIMEOUT_MS)) {
      this.refund();
      if (d.arm) d.arm.game.finish(0);
      this.end(`Lost track of ${d.name}. Your $${d.bet} is back in your pocket.`);
      return;
    }
    const arm = d.arm;
    if (!arm || d.settled) return;
    arm.sendIn -= dt;
    if (d.role === 'inviter') {
      arm.a = clamp(arm.a + (arm.game.push - arm.theirPush) * dt * 0.55, -1, 1);
      arm.game.remoteArm = arm.a;
      if (arm.sendIn <= 0) {
        arm.sendIn = 0.08;
        this.hooks.send(d.peer, { t: 'arm', id: d.id, a: arm.a });
      }
      if (Math.abs(arm.a) >= 1) {
        this.hooks.send(d.peer, { t: 'end', id: d.id, a: arm.a });
        arm.game.finish(arm.a);
        this.settleArm(d, arm.a);
      }
    } else {
      arm.game.remoteArm = -arm.a;
      if (arm.sendIn <= 0) {
        arm.sendIn = 0.08;
        this.hooks.send(d.peer, { t: 'push', id: d.id, v: arm.game.push });
      }
    }
  }

  private refund(): void {
    const d = this.duel;
    if (d?.paid && !d.settled) {
      this.s.store.earn(d.bet);
      d.settled = true;
    }
  }

  private end(message: string): void {
    this.duel = null;
    this.s.store.toast(message, 'info', ':handshake:');
  }

  /** Leaving the park mid-challenge gets your money back. */
  reset(): void {
    this.refund();
    if (this.duel?.arm) this.duel.arm.game.finish(0);
    this.duel = null;
  }
}
